/* eslint-disable react-hooks/rules-of-hooks */
import { useState, useContext, createContext } from 'react';
import PropTypes from 'prop-types';

import { trans } from '@spotahome/soyuz/client';

import * as yup from 'yup';

import FormSelect from '../FormSelect';
import FormInput from '../FormInput';
import FormDate from '../FormDate';
import Button from '../Button';

import FormPhoneInput from '../FormPhoneInput';

import FileUploaderMulti from '../FileUploaderMulti';

import FormError from './FormError';

import styles from './FormProvider.module.scss';

const noop = () => {};

const FormContext = createContext('FormContext');

export const useFormValues = () => {
  const { values, clearInput } = useContext(FormContext);
  return { values, clearInput };
};

const FormProvider = ({
  dataTest = 'form-provider',
  onSubmit = () => {},
  onError = () => {},
  schema,
  fieldsClassName = '',
  children,
  disabled = false,
  initialValues = {},
  isLoading = false,
  onInputChange = () => {}
}) => {
  const [dirtyInputs, setDirtyInputs] = useState([]);
  const [values, setValues] = useState(initialValues);
  const [errors, setFormErrors] = useState({});

  const validateField = async ({ name, value }) => {
    try {
      await yup.reach(schema, name).validate(value);
      setFormErrors({
        ...errors,
        [name]: undefined
      });
    } catch (error) {
      const { message } = error;
      setFormErrors({
        ...errors,
        [name]: message
      });
    }
  };

  const handleInputChange = async ({ value, name }) => {
    if (!dirtyInputs.includes(name)) {
      setDirtyInputs([...dirtyInputs, name]);
    }

    setValues(prevState => ({
      ...prevState,
      [name]: value
    }));

    // if the field has an error, check if new value removes it
    if (errors[name]) {
      await validateField({ name, value });
    }
    onInputChange({ value, name });
  };

  const handleInputBlur = async ({ value, name }) => {
    if (!dirtyInputs.includes(name)) {
      return;
    }

    await validateField({ name, value });
    try {
      await yup.reach(schema, name).validate(value);
      setFormErrors({
        ...errors,
        [name]: undefined
      });
    } catch (error) {
      const { message } = error;
      setFormErrors({
        ...errors,
        [name]: message
      });
    }
  };

  const handleSubmit = async e => {
    const event = Object.assign({}, e);
    e.preventDefault();
    if (disabled) {
      return;
    }

    try {
      await schema.validate(values, { abortEarly: false });
      onSubmit(values, event);
    } catch (error) {
      const newErrors = {};
      if (error && error.inner) {
        error.inner.forEach(errorItem => {
          newErrors[errorItem.path] = errorItem.message;
        });
        setFormErrors(newErrors);
      }
      onError();
    }
  };

  const clearInput = name => {
    setValues(prevState => ({
      ...prevState,
      [name]: ''
    }));
  };

  const isFieldRequired = fieldName => {
    if (schema.fields[fieldName].spec.presence !== undefined) {
      return schema.fields[fieldName].spec.presence === 'required';
    }

    const resolved = schema.describe({ value: values });

    return (
      resolved.fields[fieldName] &&
      resolved.fields[fieldName].tests.some(test => test.name === 'required')
    );
  };

  const value = {
    errors,
    values,
    disabled,
    handleInputChange,
    handleInputBlur,
    handleSubmit,
    clearInput,
    fieldsClassName,
    isLoading,
    isFieldRequired
  };

  return (
    <FormContext.Provider value={value}>
      <form data-test={dataTest} onSubmit={handleSubmit}>
        {typeof children === 'function' ? children({ values }) : children}
      </form>
    </FormContext.Provider>
  );
};

FormProvider.Input = ({
  name,
  id = name,
  innerRef,
  onBlurHandler = noop,
  ...formInputProps
}) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values,
    isFieldRequired
  } = useContext(FormContext);

  const handleBlur = (...args) => {
    handleInputBlur(...args);
    onBlurHandler();
  };

  return (
    <div className={fieldsClassName} key={id || name}>
      <FormInput
        {...formInputProps}
        value={values[name]}
        id={id}
        name={name}
        onChange={handleInputChange}
        onBlur={handleBlur}
        required={isFieldRequired(name)}
        innerRef={innerRef}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.Input.propTypes = {
  ...FormInput.propTypes,
  onBlurHandler: PropTypes.func
};

FormProvider.Date = ({ name, onChangeHandler = noop, ...formDateProps }) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values,
    isFieldRequired
  } = useContext(FormContext);

  const handleChange = args => {
    onChangeHandler(args);
    handleInputChange(args);
  };

  return (
    <div className={fieldsClassName} key={name}>
      <FormDate
        {...formDateProps}
        name={name}
        onChange={handleChange}
        onBlur={handleInputBlur}
        required={isFieldRequired(name)}
        value={values[name]}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.Date.propTypes = {
  ...FormDate.propTypes,
  onChangeHandler: PropTypes.func
};

FormProvider.PhoneInput = ({ id, name, title, required }) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values
  } = useContext(FormContext);

  return (
    <div className={fieldsClassName}>
      <FormPhoneInput
        id={id}
        name={name}
        title={title}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        required={required}
        value={values[name]}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.PhoneInput.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  required: PropTypes.bool.isRequired
};

FormProvider.Submit = ({
  id = null,
  dataTest = '',
  children,
  color = 'primary',
  onClick = () => {},
  disabled: disabledProp = false,
  ...restProps
}) => {
  const { fieldsClassName, isLoading, disabled, values } =
    useContext(FormContext);

  return (
    <div className={fieldsClassName}>
      <Button
        id={id}
        disabled={disabledProp || disabled || isLoading}
        size="big-redesign"
        color={color}
        data-test={dataTest || 'form-submit'}
        type="submit"
        onClick={() => onClick(values)}
        {...restProps}
      >
        {children}
      </Button>
    </div>
  );
};

FormProvider.Submit.propTypes = {
  id: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  dataTest: PropTypes.string,
  color: PropTypes.string,
  onClick: PropTypes.func,
  disabled: PropTypes.bool
};

FormProvider.Select = ({ name, ...formSelectProps }) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values,
    isFieldRequired
  } = useContext(FormContext);

  return (
    <div className={fieldsClassName} key={name}>
      <FormSelect
        {...formSelectProps}
        name={name}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
        required={isFieldRequired(name)}
        value={values[name]}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.Select.propTypes = FormSelect.propTypes;

FormProvider.Password = ({
  name,
  id = undefined,
  dataTest = '',
  title = '',
  placeholder = '',
  disabled = false,
  showPassword = false,
  onBlurHandler = () => {}
}) => {
  const {
    handleInputChange,
    handleInputBlur,
    errors,
    fieldsClassName,
    values,
    isFieldRequired
  } = useContext(FormContext);
  const [displayPassword, setDisplayPassword] = useState(showPassword);

  const handleBlur = (...args) => {
    handleInputBlur(...args);
    onBlurHandler();
  };

  const handleTogglePassword = () => {
    setDisplayPassword(!displayPassword);
  };

  return (
    <div className={fieldsClassName} key={id || name}>
      <FormInput
        value={values[name]}
        id={id || name}
        dataTest={dataTest}
        title={title}
        name={name}
        type={displayPassword ? 'text' : 'password'}
        placeholder={placeholder}
        onChange={handleInputChange}
        onBlur={handleBlur}
        required={isFieldRequired(name)}
        disabled={disabled}
        renderSuffix={() => (
          <Button
            color="link"
            className={styles['form-provider__password-show']}
            onClick={handleTogglePassword}
            aria-controls={id}
            aria-expanded={displayPassword}
          >
            {displayPassword
              ? trans('form-password.hide')
              : trans('form-password.show')}
          </Button>
        )}
      />
      <FormError error={trans(errors[name])} />
    </div>
  );
};

FormProvider.Password.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string.isRequired,
  dataTest: PropTypes.string,
  title: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  showPassword: PropTypes.bool,
  onBlurHandler: PropTypes.func
};

FormProvider.FileUploaderMulti = ({
  id,
  name,
  title = undefined,
  className = undefined,
  dataTest = undefined,
  required = undefined,
  maxFiles = undefined,
  onFileUploaded = undefined,
  onFileDeleted = undefined,
  onFileUploadError = undefined
}) => {
  const { fieldsClassName, handleInputChange } = useContext(FormContext);

  const handleOnFileUploaded = ({ id: idFile, filePath, files }) => {
    handleInputChange({
      value: files.map(file => file.filePath),
      name
    });

    if (typeof onFileUploaded === 'function') {
      onFileUploaded({ id: idFile, filePath, files });
    }
  };

  const handleOnDelete = ({ id: idFile, filePath, files }) => {
    handleInputChange({
      value: files.map(file => file.filePath),
      name
    });

    if (typeof onFileDeleted === 'function') {
      onFileDeleted({ id: idFile, filePath, files });
    }
  };

  return (
    <div className={fieldsClassName}>
      <FileUploaderMulti
        id={id}
        title={title}
        className={className}
        dataTest={dataTest}
        required={required}
        maxFiles={maxFiles}
        onFileUploaded={handleOnFileUploaded}
        onFileDeleted={handleOnDelete}
        onFileUploadError={onFileUploadError}
      />
    </div>
  );
};

FormProvider.FileUploaderMulti.propTypes = {
  id: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  title: PropTypes.string,
  className: PropTypes.string,
  dataTest: PropTypes.string,
  required: PropTypes.bool,
  maxFiles: PropTypes.number,
  onFileUploaded: PropTypes.func,
  onFileDeleted: PropTypes.func,
  onFileUploadError: PropTypes.func
};

FormProvider.propTypes = {
  dataTest: PropTypes.string,
  initialValues: PropTypes.shape({}),
  onSubmit: PropTypes.func,
  onError: PropTypes.func,
  onInputChange: PropTypes.func,
  schema: PropTypes.shape({
    validate: PropTypes.func,
    describe: PropTypes.func,
    fields: PropTypes.shape({
      [PropTypes.string]: PropTypes.shape({})
    })
  }).isRequired,
  children: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  fieldsClassName: PropTypes.string,
  isLoading: PropTypes.bool,
  disabled: PropTypes.bool
};

export default FormProvider;
